import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePolygonClient } from "./usePolygonClient";
import { useAuth } from "./useAuth";
import { useUIStore } from "@/stores/useUIStore";
import { useNetworkStore, AMOY_CHAIN_ID } from "@/stores/useNetworkStore";

/** What one tap mints. Enough to fund a handful of test quests. */
export const TEST_USDT_MINT_AMOUNT = 1000;

/**
 * Mint test USDT into the signed-in wallet. Testnet only.
 *
 * The Amoy token is a MockERC20 whose mint() is open to anyone, so this is the
 * testnet faucet. `available` is false on every other chain (there is no mint
 * on real USDT) and while the wallet or client isn't ready, so callers render
 * nothing rather than a button that can only fail.
 *
 * A mutation, not local state: the network switch disables itself while any
 * mutation is in flight, so a reload can't land mid-mint. On success the
 * wallet-balances query is invalidated, so the menu's balance card shows the
 * new number at once instead of on its next 60s poll.
 */
export function useMintTestUsdt() {
  const { t } = useTranslation();
  const polygonClient = usePolygonClient();
  const { session } = useAuth();
  const chainId = useNetworkStore((s) => s.chainId);
  const addToast = useUIStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const walletAddress = session?.walletAddress;

  const mutation = useMutation({
    mutationKey: ["mint-test-usdt", walletAddress],
    mutationFn: async () => {
      if (!polygonClient || !walletAddress) throw new Error("Wallet not ready");
      return polygonClient.mintTestToken("USDT", walletAddress, TEST_USDT_MINT_AMOUNT);
    },
    onSuccess: () => {
      addToast({ type: "success", message: t("testnet.mintSuccess") });
      void queryClient.invalidateQueries({ queryKey: ["wallet-balances"] });
    },
    onError: (err) => {
      console.error("[MintTestUSDT]", err);
      addToast({ type: "error", message: t("testnet.mintFailed") });
    },
  });

  return {
    available: chainId === AMOY_CHAIN_ID && !!polygonClient && !!walletAddress,
    mint: () => mutation.mutate(),
    minting: mutation.isPending,
  };
}
